import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { DashboardLayout, Card, FormInput, Button, TermsAcceptanceHistory } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { adminApi } from '../services/api';
import type { AdminInspector } from '../types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

type InspectorVerificationStatus = 'Verifierad' | 'Ej Verifierad';
type TabType = 'details' | 'projects';

interface InspectorFormData {
  name: string;
  organizationNumber: string;
  inspectorCompanyName: string;
  email: string;
  phone: string;
  vatNumber: string;
  website: string;
  inspectorPresentation: string;
  verifiedInspector: InspectorVerificationStatus;
  address: string;
  postalCode: string;
  city: string;
  notes: string;
}

export function AdminInspectorView() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [inspector, setInspector] = useState<AdminInspector | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState<InspectorFormData>({
    name: '',
    organizationNumber: '',
    inspectorCompanyName: '',
    email: '',
    phone: '',
    vatNumber: '',
    website: '',
    inspectorPresentation: '',
    verifiedInspector: 'Ej Verifierad',
    address: '',
    postalCode: '',
    city: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const inspectors = await adminApi.getAllInspectors();
      const foundInspector = inspectors.find(i => i.id === id);
      if (foundInspector) {
        setInspector(foundInspector);
        setFormData({
          name: foundInspector.name || '',
          organizationNumber: foundInspector.organizationNumber || '',
          inspectorCompanyName: foundInspector.inspectorCompanyName || '',
          email: foundInspector.email || '',
          phone: foundInspector.phone || '',
          vatNumber: foundInspector.vatNumber || '',
          website: foundInspector.website || '',
          inspectorPresentation: foundInspector.inspectorPresentation || '',
          verifiedInspector: (foundInspector.verifiedInspector as InspectorVerificationStatus) || 'Ej Verifierad',
          address: foundInspector.address || '',
          postalCode: foundInspector.postalCode || '',
          city: foundInspector.city || '',
          notes: foundInspector.notes || ''
        });
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNotesChange = (value: string) => {
    setFormData(prev => ({ ...prev, notes: value }));
  };

  const handleInspectorPresentationChange = (value: string) => {
    setFormData(prev => ({ ...prev, inspectorPresentation: value }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email.trim()) {
      errors.email = t('validation.required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('validation.email');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !inspector) return;

    setIsSaving(true);
    setMessage(null);

    try {
      const { name: _verifiedName, email: _verifiedEmail, ...details } = formData;
      await adminApi.updateInspector(inspector.id, details);
      setMessage({ type: 'success', text: t('inspectors.form.saveSuccess') });
      await loadData();
    } catch (error) {
      console.error('Failed to update inspector:', error);
      setMessage({ type: 'error', text: t('inspectors.form.saveError') });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!inspector) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">
          <h4>{t('inspectorView.notFound')}</h4>
          <Link to="/admin/inspectors" className="btn btn-primary mt-3">
            {t('common.back')}
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {t('inspectorView.title')} - {inspector.inspectorNumber} - {inspector.name}
            </h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('adminInspectors.title'), path: '/admin/inspectors' },
            { label: t('inspectorView.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('inspectorView.description')}</p>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <div className="d-flex gap-2">
            <Link to="/admin/inspectors" className="btn btn-light-brand">
              <i className="feather-arrow-left me-2"></i>
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div className="nav-tabs-wrapper">
          <ul className="nav nav-tabs nav-tabs-custom-style" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                {t('inspectorView.tabs.details')}
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'projects' ? 'active' : ''}`}
                onClick={() => setActiveTab('projects')}
              >
                {t('inspectorView.tabs.projects')}
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Details Tab */}
        <div className={`tab-pane fade ${activeTab === 'details' ? 'show active' : ''}`}>
          <Card>
            <div className="card-header">
              <h5 className="card-title mb-0">{t('inspectorView.tabs.details')}</h5>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="row">
                  {/* Inspector Number (read-only) */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('inspectors.columns.inspectorNumber')}</label>
                    <input
                      type="text"
                      className="form-control bg-light"
                      value={inspector.inspectorNumber}
                      disabled
                    />
                  </div>

                  {/* Verified Status */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">{t('inspectors.columns.verifiedInspector')}</label>
                    <select
                      name="verifiedInspector"
                      className="form-select"
                      value={formData.verifiedInspector}
                      onChange={handleInputChange}
                    >
                      <option value="Ej Verifierad">{t('inspectors.status.notVerified')}</option>
                      <option value="Verifierad">{t('inspectors.status.verified')}</option>
                    </select>
                  </div>
                </div>

                <div className="row">
                  {/* Name */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="name"
                      label={t('inspectors.columns.name')}
                      value={formData.name}
                      readOnly
                      helpText={t('auth.bankid.nameLocked')}
                      maxLength={128}
                    />
                  </div>

                  {/* Organization Number */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="organizationNumber"
                      label={t('inspectors.columns.organizationNumber')}
                      value={formData.organizationNumber}
                      onChange={handleInputChange}
                      maxLength={32}
                    >
                      <TestOrgNumberButton onGenerated={value => { setFormData(previous => withTestOrgNumber(previous, value)); setFormErrors(previous => ({ ...previous, organizationNumber: '' })); }} />
                    </FormInput>
                  </div>
                </div>

                <div className="row">
                  {/* Inspector Company Name */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="inspectorCompanyName"
                      label={t('inspectors.columns.inspectorCompanyName')}
                      value={formData.inspectorCompanyName}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                  {/* Phone */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      type="tel"
                      name="phone"
                      label={t('inspectors.columns.phone')}
                      value={formData.phone}
                      onChange={handleInputChange}
                      maxLength={48}
                    />
                  </div>
                </div>

                <div className="row">
                  {/* VAT Number */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="vatNumber"
                      label={t('inspectors.columns.vatNumber')}
                      value={formData.vatNumber}
                      onChange={handleInputChange}
                      maxLength={32}
                    />
                  </div>

                  {/* Contact Email */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="email"
                      type="email"
                      label={`${t('inspectors.columns.email')} *`}
                      value={formData.email}
                      readOnly
                      error={formErrors.email}
                      maxLength={128}
                    />
                  </div>
                </div>

                <div className="row">
                  {/* Website */}
                  <div className="col-md-6 mb-3">
                    <FormInput
                      name="website"
                      label={t('inspectors.columns.website')}
                      value={formData.website}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                </div>

                <div className="row">
                  {/* Address */}
                  <div className="col-md-4 mb-3">
                    <AddressAutocomplete
                      name="address"
                      onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                      label={t('inspectors.columns.address')}
                      value={formData.address}
                      onChange={handleInputChange}
                      maxLength={128}
                    />
                  </div>

                  {/* Postal Code */}
                  <div className="col-md-4 mb-3">
                    <FormInput
                      name="postalCode"
                      label={t('inspectors.columns.postalCode')}
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      maxLength={16}
                    />
                  </div>

                  {/* City */}
                  <div className="col-md-4 mb-3">
                    <FormInput
                      name="city"
                      label={t('inspectors.columns.city')}
                      value={formData.city}
                      onChange={handleInputChange}
                      maxLength={64}
                    />
                  </div>
                </div>

                {/* Company Presentation (Rich Editor) */}
                <div className="mb-4">
                  <label className="form-label">{t('inspectors.columns.inspectorPresentation')}</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.inspectorPresentation}
                    onChange={handleInspectorPresentationChange}
                    style={{ height: '150px' }}
                  />
                </div>
                {/* Spacer for Quill toolbar */}
                <div style={{ height: '45px' }}></div>

                {/* Notes (Rich Editor) */}
                <div className="mb-4">
                  <label className="form-label">{t('inspectors.columns.notes')}</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.notes}
                    onChange={handleNotesChange}
                    style={{ height: '150px' }}
                  />
                </div>
                {/* Spacer for Quill toolbar */}
                <div style={{ height: '45px' }}></div>
              </div>
              <div className="card-footer d-flex justify-content-end">
                <Button type="submit" isLoading={isSaving}>
                  <i className="feather-save me-2"></i>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Card>
          {inspector && <TermsAcceptanceHistory userId={inspector.id} />}
        </div>

        {/* Projects Tab */}
        <div className={`tab-pane fade ${activeTab === 'projects' ? 'show active' : ''}`}>
          <Card>
            <div className="card-header">
              <h5 className="card-title mb-0">{t('inspectorView.tabs.projects')}</h5>
            </div>
            <div className="card-body">
              <p className="text-muted mb-0">{t('inspectorView.noProjects')}</p>
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
